import React, { useState, useRef, useEffect } from "react";
import { ScratchCardPage as PageType } from "../../types/quiz";
import '../../assets/scss/quiz-editorial.scss';

interface Props {
  page: PageType;
  onNext: () => void;
}

const ScratchCardPage: React.FC<Props> = ({ page, onNext }) => {
  const [isRevealed, setIsRevealed] = useState(false);
  const [timeLeft, setTimeLeft] = useState(page.timerDuration || 600);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isScratching, setIsScratching] = useState(false);

  // Timer countdown
  useEffect(() => {
    if (!page.timerDuration) return;

    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [page.timerDuration]);

  // Initialize scratch canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Set canvas size
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    // Draw scratch layer
    ctx.fillStyle = "#CCCCCC";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.font = "24px Arial";
    ctx.fillStyle = "#666";
    ctx.textAlign = "center";
    ctx.fillText("Scratch Here", canvas.width / 2, canvas.height / 2);
  }, []);

  const scratch = (
    e:
      | React.MouseEvent<HTMLCanvasElement>
      | React.TouchEvent<HTMLCanvasElement>,
  ) => {
    const canvas = canvasRef.current;
    if (!canvas || isRevealed) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    let x, y;

    if ("touches" in e) {
      x = e.touches[0].clientX - rect.left;
      y = e.touches[0].clientY - rect.top;
    } else {
      x = e.clientX - rect.left;
      y = e.clientY - rect.top;
    }

    ctx.globalCompositeOperation = "destination-out";
    ctx.beginPath();
    ctx.arc(x, y, 30, 0, Math.PI * 2);
    ctx.fill();

    // Check if enough is scratched (simple check)
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const pixels = imageData.data;
    let transparent = 0;

    for (let i = 3; i < pixels.length; i += 4) {
      if (pixels[i] === 0) transparent++;
    }

    const percentScratched = (transparent / (pixels.length / 4)) * 100;

    if (percentScratched > 50) {
      setIsRevealed(true);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className="page page--scratch-card">
      <div className="page__container">
        <h2 className="page__question">{page.title}</h2>
        {page.subtitle && <p className="page__subtitle">{page.subtitle}</p>}

        {/* Timer */}
        {page.timerDuration && timeLeft > 0 && (
          <div className="countdown-timer">
            <span className="countdown-timer__icon">⏰</span>
            <span className="countdown-timer__time">
              {formatTime(timeLeft)}
            </span>
          </div>
        )}

        {/* Scratch Card */}
        <div className="scratch-card">
          <div className="scratch-card__revealed">
            <h3 className="scratch-card__discount">{page.discountValue}</h3>
            <p>Your exclusive discount!</p>
          </div>
          {!isRevealed && (
            <canvas
              ref={canvasRef}
              className="scratch-card__canvas"
              onMouseDown={() => setIsScratching(true)}
              onMouseUp={() => setIsScratching(false)}
              onMouseMove={(e) => isScratching && scratch(e)}
              onTouchStart={() => setIsScratching(true)}
              onTouchEnd={() => setIsScratching(false)}
              onTouchMove={(e) => scratch(e)}
            />
          )}
        </div>

        <button
          className="page__button"
          onClick={onNext}
          disabled={!isRevealed}
        >
          {page.buttonText}
        </button>
      </div>
    </div>
  );
};

export default ScratchCardPage;
